import React from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, Users, Shield, Zap, MessageCircle } from 'lucide-react';

const DocumentationIndexPage = () => {
  const documentationItems = [
    {
      title: 'User Guide',
      description: 'Complete system overview with step-by-step instructions',
      icon: Users,
      path: '/documentation/user-guide',
      features: ['Getting started', 'Feature explanations', 'Best practices', 'Troubleshooting']
    },
    {
      title: 'Admin Guide',
      description: 'Essential information for group administrators',
      icon: Shield,
      path: '/documentation/admin-guide',
      features: ['Administrative responsibilities', 'Group setup and configuration', 'Financial management', 'Security and access control']
    },
    {
      title: 'Quick Reference',
      description: 'Fast access to common tasks and essential information',
      icon: Zap,
      path: '/documentation/quick-reference',
      features: ['Common actions', 'Keyboard shortcuts', 'Status meanings', 'Troubleshooting quick fixes']
    },
    {
      title: 'FAQ',
      description: 'Answers to the most common questions',
      icon: MessageCircle,
      path: '/documentation/faq',
      features: ['General questions', 'Technical issues', 'Feature explanations', 'Support information']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <HelpCircle className="h-16 w-16 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Twachanga Documentation
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive guides to help you manage your group effectively
          </p>
        </div>

        {/* Quick Start */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">🚀 Quick Start</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              'Register your group account',
              'Complete your group profile',
              'Add your group logo',
              'Configure M-Pesa integration',
              'Add your first members',
              'Set up contribution drives',
              'Create group policies'
            ].map((step, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <span className="text-gray-700">{step}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Documentation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {documentationItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={index}
                to={item.path}
                className="bg-white rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:shadow-md transition-shadow duration-200 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <IconComponent className="h-8 w-8 text-blue-600 group-hover:text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {item.description}
                    </p>
                    <ul className="space-y-2">
                      {item.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-sm text-gray-500">
                          <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Features Overview */}
        <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">🎯 Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Member Management',
                description: 'Add and manage group members, bulk upload, member profiles and photos, status management'
              },
              {
                title: 'Financial Management',
                description: 'Contribution tracking, M-Pesa integration, loan management, share management'
              },
              {
                title: 'Reporting & Analytics',
                description: 'Financial reports, member statistics, contribution analysis, export capabilities'
              },
              {
                title: 'Communication',
                description: 'Bulk SMS messaging, meeting management, document sharing, notifications'
              }
            ].map((feature, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-12 bg-blue-50 rounded-lg border border-blue-200 p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">📞 Need Help?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Contact our support team:</h3>
              <div className="space-y-2 text-gray-600">
                <p>📧 Email: <a href="mailto:help@twachanga.co.ke" className="text-blue-600 hover:text-blue-800">help@twachanga.co.ke</a></p>
                <p>📞 Phone: +254739264525</p>
                <p>📱 WhatsApp: +254739264525</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Business Hours:</h3>
              <div className="space-y-2 text-gray-600">
                <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                <p>Saturday: 9:00 AM - 1:00 PM</p>
                <p>Sunday: Closed</p>
                <p>Emergency: 24/7 support line</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationIndexPage; 